

import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { Router } from '@angular/router';
import { ApiService } from 'src/app/services/api/api.service';
import { NgSelectComponent } from '@ng-select/ng-select';
import { SuccessModalComponent } from 'src/app/shared/modals/success-modal/success-modal.component';
import { ConfirmActionComponent } from 'src/app/shared/components/confirm-action/confirm-action.component';
import { ErrorModalComponent } from 'src/app/shared/modals/error-modal/error-modal.component';
import { UtilitiesService } from 'src/app/services/utilities/utilities.service';

@Component({
  selector: 'app-property-asset-details-modal',
  standalone: false,
  templateUrl: './property-asset-details-modal.component.html',
  styleUrl: './property-asset-details-modal.component.scss'
})
export class PropertyAssetDetailsModalComponent {

  @ViewChild("assetImageInput", { static: false })
  assetImageInput!: ElementRef<HTMLInputElement>;

  form!: FormGroup;
  isSubmitting: boolean = false;
  isSubmitAttempted: boolean = false;
  propertyTypeList: any[];
  propertyCategoryList: any[];
  isLoading: boolean = false;

  showErrorMessages: boolean = false;


  lgaList: any;
  zoneList: any;

  constructor(
    private dialog: MatDialog,
    private router: Router,
    public dialogRef: MatDialogRef<PropertyAssetDetailsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private apiService: ApiService,
    private formBuilder: FormBuilder,
    private utilitiesService: UtilitiesService
  ) { }

  ngOnInit(): void {
    this.initializeForm();

    this.fetchPropertyTypes();

    this.subscribeToTransportChange()

    if (this.dialogData) {
      this.patchForm(this.dialogData)
    }

  }

  get lgaControl() {
    return this.form.get('lga') as FormControl
  }

  get zoneControl() {
    return this.form.get('zone') as FormControl
  }



  initializeForm() {
    this.form = this.formBuilder.group({
      address: [null, [Validators.required]],
      type: [null, [Validators.required,],],
      category: [null, [Validators.required]],
      assetImage: [null, [Validators.required]],
    });

  }

  patchForm(details) {
    this.form.patchValue({
      type: details.typeId,
      category: details.categoryId,
      address: details.address,
      assetImage: {readableUrl: details.photo}
    })
  }

  phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value === null || control.value === undefined) {
        return null;
      }

      const cleanedPhoneNumber = control.value
        .replace(/\s/g, '')
        .replace(/-/g, '');

      const isDigitsOnly = /^\d+$/.test(cleanedPhoneNumber);

      if (
        cleanedPhoneNumber.length === 0 ||
        (cleanedPhoneNumber.length === 11 && isDigitsOnly) ||
        (cleanedPhoneNumber.length === 13 &&
          cleanedPhoneNumber.startsWith('234')) ||
        (cleanedPhoneNumber.length === 14 &&
          cleanedPhoneNumber.startsWith('+234'))
      ) {
        return null;
      } else {
        return { invalidPhoneNumber: true };
      }
    };
  }

  emailValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

      if (control.value && !emailPattern.test(control.value)) {
        return { invalidEmail: true };
      }

      return null;
    };
  }

  openSuccessModal(message: string) {
    let dialogRef = this.dialog.open(SuccessModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,

      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { successMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => {

      this.dialogRef.close({ reload: true })
    });
  }

  openErrorModal(message: string) {
    let dialogRef = this.dialog.open(ErrorModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form',],
      data: { errorMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => { });
  }

  openConfirmModal() {
    this.dialog.open(ConfirmActionComponent, {
      width: '400px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'operation-modal',],
      disableClose: true,

      data: {
        title: 'Are you sure you want to edit these details?',
        body: '',
        confirmText: 'Yes',
        lottieIconPath: 'assets/lottie/alert.json',
        onConfirm: () => {
          this.submit();
        },
        onCancel: () => { },
        showCancelButton: true,
        showConfirmButton: true,
      },
    });
  }


  get assetImageControl() {
    return this.form.get('assetImage') as FormControl
  }


  triggerUploadAssetImage() {
    this.assetImageInput.nativeElement.value = null
    this.assetImageInput.nativeElement.click();
  }

  onAssetImageChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      const file = input.files[0];

      if (file.size <= 1 * 1024 * 1024) {
        const reader = new FileReader();

        reader.onload = () => {
          const result = reader.result as string;
          const base64 = result.split(',')[1];
          const uploadedImageUrl = URL.createObjectURL(file);
          this.form?.patchValue({
            assetImage: {
              base64,
              readableUrl: uploadedImageUrl
            }
          });
          this.form.markAsDirty();
        };

        reader.readAsDataURL(file);
      } else {
        input.value = null;
        this.assetImageControl.setErrors({ maxFileSize: 1 * 1024 * 1024 });
      
      }
    }
  }



  async submit(): Promise<void> {
    if (this.form.valid) {
      this.isSubmitting = true;

      try {
        const formData = this.form.value;
        const request = await this.mapFormValuesToObject(formData);

        const url = '/property/update';

        this.apiService
          .post(url, request)
          .pipe(finalize(() => (this.isSubmitting = false)))
          .subscribe(
            (res) => {
              this.openSuccessModal(res.message || 'Asset details updated successfully');
            },
            (error) => {
              this.openErrorModal(error?.message);
            }
          );
      } catch (error) {
        this.isSubmitting = false;
        this.openErrorModal('Failed to process form data');
      }
    }
  }

  async mapFormValuesToObject(formValue: any): Promise<any> {
    return {
      propertyId: this.dialogData.id,
      typeId: formValue.type,
      categoryId: formValue.category,
      address: formValue.address,
      assetImage: formValue.assetImage.base64
    };
  }

  fetchPropertyTypes() {
    this.isLoading = true;
    this.apiService
      .get('/property/all/building/type')
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((response: any) => {
        this.propertyTypeList = response.map((item) => ({
          ...item,
          formattedName: this.toTitleCase(item.name),
        }));
      });
  }

  get typeControl() {
    return this.form.get('type') as FormControl;
  }

  get categoryControl() {
    return this.form.get('category') as FormControl;
  }

  subscribeToTransportChange() {
    this.typeControl.valueChanges.subscribe(transportId => {
      this.fetchCategoriesByTypeId(transportId);
      this.categoryControl.reset()
    })
  }

  fetchCategoriesByTypeId(transportId: number) {
    this.isLoading = true;
    this.apiService
      .get('/property/all/building/category/' + transportId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((response: any) => {
        this.propertyCategoryList = response.map((item) => ({
          ...item,
          formattedName: this.toTitleCase(item.name.replace(/_/g, ' ')),
        }));
      });
  }










  toTitleCase(str: string): string {
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  }

  closeModal(): void {
    this.form.reset();
    this.dialogRef.close();
  }
}
